import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Platform, Modal, Image, useWindowDimensions, ScrollView, KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import WebScrollView from '../../components/WebScrollView';
import { Search, Send, User, Plus, X, Mail, Paperclip, Bot, ChevronLeft, ChevronDown } from '../../lib/icons';
import { showAlert, openExternalLink } from '../../lib/platform';
import { supabase } from '../../lib/supabase';
import { useConversationMessages } from '../../hooks/useConversationMessages';

interface Conversation {
  id: string;
  homeowner_id: string;
  type: string;
  home_manager_id: string | null;
  home_id: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  homeowner_unread_count: number;
  manager_unread_count: number;
  ai_mode: boolean;
  // Conversation engine fields
  current_state: string | null;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'cancelled' | 'no-response';
  message_count: number;
  complexity_score: number;
  pending_ai_suggestion: string | null;
  human_takeover_by: string | null;
  human_takeover_at: string | null;
  human_takeover_reason: string | null;
  context: Record<string, any> | null;
  service_request_ids: string[] | null;
  human_takeover_active: boolean;
  last_complexity_check_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  error_message: string | null;
  homeowner: {
    id: string;
    full_name: string;
    profile_photo_url: string | null;
  };
  home?: {
    id: string;
    address: string;
    city: string;
    state: string;
  } | null;
}

interface ConversationMessage {
  id: string;
  conversation_id: string;
  sender_type: 'homeowner' | 'manager' | 'ai';
  sender_id: string | null;
  content: string;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_size: number | null;
  attachment_type: string | null;
  created_at: string;
  conversation_state: string | null;
  message_id: string | null;
  sender?: {
    full_name: string;
  };
}

const ConversationsTab = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [homeownerSearchQuery, setHomeownerSearchQuery] = useState('');
  const [availableHomeowners, setAvailableHomeowners] = useState<Array<{
    id: string;
    full_name: string;
    profile_photo_url: string | null;
  }>>([]);
  const [loadingHomeowners, setLoadingHomeowners] = useState(false);
  const [hasMarkedAsRead, setHasMarkedAsRead] = useState<string | null>(null);
  const [modeFilter, setModeFilter] = useState<'all' | 'human' | 'ai'>('all');
  const [showAISidebarMobile, setShowAISidebarMobile] = useState(false);

  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  // Safe-area insets are needed on iOS to push the input above the keyboard
  // by the right amount: AdminLayout's mobileHeader sits at the top with
  // paddingTop = insets.top, so we offset the keyboard avoiding view by
  // (insets.top + header content height).
  const safeAreaInsets = Platform.OS === 'web'
    ? { top: 0, bottom: 0, left: 0, right: 0 }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    : useSafeAreaInsets();
  // AdminLayout mobile header: paddingTop 12 + content (~30) + paddingBottom 12 ≈ 54
  const ADMIN_MOBILE_HEADER_HEIGHT = 54;
  const keyboardVerticalOffset = isMobile
    ? safeAreaInsets.top + ADMIN_MOBILE_HEADER_HEIGHT
    : 0;
  // On mobile, show list when no conversation is selected, otherwise show detail
  const showListPanel = !isMobile || !selectedConversation;
  const showDetailPanel = !isMobile || !!selectedConversation;

  const { messages, loading: messagesLoading, refetch } = useConversationMessages(
    selectedConversation?.id || null
  );

  // --- Chat scroll behavior (jump-to-latest, auto-scroll on new messages) ---
  const messagesScrollRef = useRef<ScrollView | null>(null);
  // Tracks whether the user is "near the bottom" of the messages list.
  // We auto-scroll on new messages only when this is true.
  const isNearBottomRef = useRef<boolean>(true);
  // Tracks the previous message count so we can detect new messages and
  // distinguish initial-load from incremental updates.
  const prevMessageCountRef = useRef<number>(0);
  const prevConversationIdRef = useRef<string | null>(null);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [unreadNewMessageCount, setUnreadNewMessageCount] = useState(0);

  const NEAR_BOTTOM_THRESHOLD_PX = 120;

  const scrollMessagesToBottom = useCallback((animated: boolean) => {
    // Use a couple of rAFs to wait for the latest message layout to settle
    // (e.g. wrapping text, image placeholders) before measuring/scrolling.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesScrollRef.current?.scrollToEnd({ animated });
      });
    });
  }, []);

  const handleMessagesScroll = useCallback(
    (event: { nativeEvent: { contentOffset: { y: number }; contentSize: { height: number }; layoutMeasurement: { height: number } } }) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
      const nearBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
      isNearBottomRef.current = nearBottom;
      if (nearBottom) {
        // User has scrolled (or scrolled back) to the bottom – clear the pill.
        if (showScrollToLatest) setShowScrollToLatest(false);
        if (unreadNewMessageCount > 0) setUnreadNewMessageCount(0);
      }
    },
    [showScrollToLatest, unreadNewMessageCount]
  );

  // Reset scroll/state when switching conversations.
  useEffect(() => {
    prevConversationIdRef.current = selectedConversation?.id || null;
    prevMessageCountRef.current = 0;
    isNearBottomRef.current = true;
    setShowScrollToLatest(false);
    setUnreadNewMessageCount(0);
  }, [selectedConversation?.id]);

  // React to messages changing: initial load vs new incoming messages.
  useEffect(() => {
    if (!selectedConversation || messagesLoading) return;
    const messageCount = messages.length;
    const prevCount = prevMessageCountRef.current;

    if (messageCount === 0) {
      prevMessageCountRef.current = 0;
      return;
    }

    if (prevCount === 0) {
      // Initial load for this conversation – jump to most recent message.
      scrollMessagesToBottom(false);
      // Re-attempt after a beat to handle late-loading images/avatars.
      const t = setTimeout(() => scrollMessagesToBottom(false), 250);
      prevMessageCountRef.current = messageCount;
      return () => clearTimeout(t);
    }

    if (messageCount > prevCount) {
      const newMessages = messages.slice(prevCount);
      const hasOwnMessage = newMessages.some(
        (m) => m.sender_type === 'manager' && m.sender_id === currentUserId
      );

      if (hasOwnMessage || isNearBottomRef.current) {
        scrollMessagesToBottom(true);
        setShowScrollToLatest(false);
        setUnreadNewMessageCount(0);
      } else {
        // New messages arrived while user is scrolled up – surface a pill.
        setShowScrollToLatest(true);
        setUnreadNewMessageCount((c) => c + newMessages.length);
      }
    }

    prevMessageCountRef.current = messageCount;
  }, [messages, messagesLoading, selectedConversation, currentUserId, scrollMessagesToBottom]);

  const handleScrollToLatestPress = useCallback(() => {
    scrollMessagesToBottom(true);
    setShowScrollToLatest(false);
    setUnreadNewMessageCount(0);
    isNearBottomRef.current = true;
  }, [scrollMessagesToBottom]);

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if user is a system manager
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      let query = supabase
        .from('conversations')
        .select(`
          *,
          homeowner:profiles!conversations_homeowner_id_fkey (
            id,
            full_name,
            profile_photo_url
          ),
          home:homes!conversations_home_id_fkey (
            id,
            address,
            city,
            state
          )
        `)
        .eq('type', 'home_manager')
        .order('last_message_at', { ascending: false, nullsFirst: false});

      // If not a system manager, filter by assigned conversations
      if (!profile?.is_system_manager) {
        query = query.eq('home_manager_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = useCallback(async (conversationId: string) => {
    if (!conversationId) {
      console.warn('Cannot mark messages as read: conversationId is missing');
      return;
    }

    try {
      console.log('Marking messages as read for conversation:', conversationId);
      
      // Workaround: Update directly instead of using RPC function
      // This bypasses the UUID serialization issue
      const now = new Date().toISOString();
      
      // Mark messages as read by manager
      const { error: updateError } = await supabase
        .from('conversation_messages')
        .update({ read_by_manager_at: now })
        .eq('conversation_id', conversationId)
        .is('read_by_manager_at', null)
        .eq('sender_type', 'homeowner');

      if (updateError) {
        console.error('Error updating messages:', updateError);
        throw updateError;
      }

      // Reset manager unread count
      const { error: convError } = await supabase
        .from('conversations')
        .update({ manager_unread_count: 0 })
        .eq('id', conversationId);

      if (convError) {
        console.error('Error updating conversation:', convError);
        throw convError;
      }

      // Cancel pending notifications (get message IDs first)
      const { data: messages } = await supabase
        .from('conversation_messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('sender_type', 'homeowner')
        .not('read_by_manager_at', 'is', null);

      if (messages && messages.length > 0) {
        const messageIds = messages.map(m => m.id);
        await supabase
          .from('message_notifications')
          .update({ status: 'cancelled' })
          .in('message_id', messageIds)
          .eq('status', 'pending');
      }

      console.log('Successfully marked messages as read');

      // Update local state
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, manager_unread_count: 0 }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, []);

  const markMessagesAsUnread = useCallback(async (conversationId: string) => {
    if (!conversationId) {
      console.warn('Cannot mark messages as unread: conversationId is missing');
      return;
    }

    try {
      console.log('Marking messages as unread for conversation:', conversationId);
      
      // Count currently unread homeowner messages
      const { data: unreadMessages, error: countError } = await supabase
        .from('conversation_messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('sender_type', 'homeowner')
        .is('read_by_manager_at', null);

      if (countError) {
        console.error('Error counting messages:', countError);
        throw countError;
      }

      const currentUnreadCount = unreadMessages?.length || 0;

      // If there are no unread messages, mark the most recent homeowner message as unread
      if (currentUnreadCount === 0) {
        const { data: recentMessage, error: recentError } = await supabase
          .from('conversation_messages')
          .select('id')
          .eq('conversation_id', conversationId)
          .eq('sender_type', 'homeowner')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (recentError) {
          console.error('Error finding recent message:', recentError);
          // If no homeowner messages exist, just set count to 0
          const { error: convError } = await supabase
            .from('conversations')
            .update({ manager_unread_count: 0 })
            .eq('id', conversationId);
          
          if (!convError) {
            setConversations(prev =>
              prev.map(conv =>
                conv.id === conversationId
                  ? { ...conv, manager_unread_count: 0 }
                  : conv
              )
            );
          }
          return;
        }

        if (recentMessage) {
          // Mark the most recent message as unread
          const { error: updateError } = await supabase
            .from('conversation_messages')
            .update({ read_by_manager_at: null })
            .eq('id', recentMessage.id);

          if (updateError) {
            console.error('Error marking message as unread:', updateError);
            throw updateError;
          }
        }
      }

      // Recalculate unread count after potential update
      const { data: updatedUnreadMessages } = await supabase
        .from('conversation_messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('sender_type', 'homeowner')
        .is('read_by_manager_at', null);

      const newUnreadCount = updatedUnreadMessages?.length || 0;
      
      // Update manager unread count
      const { error: convError } = await supabase
        .from('conversations')
        .update({ manager_unread_count: newUnreadCount })
        .eq('id', conversationId);

      if (convError) {
        console.error('Error updating conversation:', convError);
        throw convError;
      }

      console.log('Successfully marked messages as unread, new count:', newUnreadCount);

      // Update local state
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, manager_unread_count: newUnreadCount }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking messages as unread:', error);
      showAlert('Failed to mark conversation as unread. Please try again.');
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchConversations();

    // Set up realtime subscription for conversations
    if (Platform.OS === 'web') {
      const channel = supabase
        .channel('admin-conversations')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: 'type=eq.home_manager'
          },
          () => {
            fetchConversations();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, []);

  useEffect(() => {
    // Reset hasMarkedAsRead when conversation changes
    setHasMarkedAsRead(null);
    // Close mobile AI sidebar when switching conversations
    setShowAISidebarMobile(false);
  }, [selectedConversation?.id]);

  useEffect(() => {
    // Mark messages as read when conversation is selected and messages are loaded
    if (selectedConversation && currentUserId && !messagesLoading && messages.length > 0) {
      // Only mark as read once per conversation selection
      if (hasMarkedAsRead !== selectedConversation.id) {
        console.log('Marking messages as read for conversation:', selectedConversation.id);
        markMessagesAsRead(selectedConversation.id);
        setHasMarkedAsRead(selectedConversation.id);
      }
    }
  }, [selectedConversation?.id, currentUserId, messagesLoading, messages.length, hasMarkedAsRead, markMessagesAsRead]);

  const handleFileSelect = () => {
    if (Platform.OS === 'web' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        showAlert('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `message-attachments/${user.id}/${timestamp}_${sanitizedFileName}`;

    const { error: uploadError } = await supabase.storage
      .from('message-attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('message-attachments')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !selectedFile) || !selectedConversation || !currentUserId) return;

    const content = messageInput.trim();
    setSendingMessage(true);
    setUploadingFile(selectedFile !== null);

    try {
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;
      let attachmentSize: number | null = null;
      let attachmentType: string | null = null;

      // Upload file if selected
      if (selectedFile) {
        try {
          attachmentUrl = await uploadFile(selectedFile);
          attachmentName = selectedFile.name;
          attachmentSize = selectedFile.size;
          attachmentType = selectedFile.type || 'application/octet-stream';
        } catch (error) {
          console.error('Error uploading file:', error);
          showAlert('Failed to upload file. Please try again.');
          setSendingMessage(false);
          setUploadingFile(false);
          return;
        }
      }

      // Insert message with or without attachment
      const messageData: any = {
        conversation_id: selectedConversation.id,
        sender_type: 'manager',
        sender_id: currentUserId,
      };

      if (content) {
        messageData.content = content;
      }

      if (attachmentUrl) {
        messageData.attachment_url = attachmentUrl;
        messageData.attachment_name = attachmentName;
        messageData.attachment_size = attachmentSize;
        messageData.attachment_type = attachmentType;
      }

      const { error } = await supabase
        .from('conversation_messages')
        .insert(messageData);

      if (error) throw error;

      // Clear inputs
      setMessageInput('');
      setSelectedFile(null);
      setUploadingFile(false);

      // Immediately refetch messages to show the sent message
      // Real-time subscription will also update, but this ensures immediate display
      refetch();
    } catch (error) {
      console.error('Error sending message:', error);
      showAlert('Failed to send message');
    } finally {
      setSendingMessage(false);
      setUploadingFile(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.homeowner.full_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesModeFilter = 
      modeFilter === 'all' ||
      (modeFilter === 'ai' && conv.ai_mode === true) ||
      (modeFilter === 'human' && conv.ai_mode === false);
    return matchesSearch && matchesModeFilter;
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  };

  const searchHomeowners = async (query: string) => {
    const trimmedQuery = query.trim();
    console.log('searchHomeowners called with query:', trimmedQuery);
    
    if (!trimmedQuery) {
      console.log('Query is empty, clearing results');
      setAvailableHomeowners([]);
      return;
    }

    try {
      setLoadingHomeowners(true);
      console.log('Starting search for:', trimmedQuery);
      
      // Use ilike with proper escaping for Supabase
      const searchPattern = `%${trimmedQuery}%`;
      console.log('Search pattern:', searchPattern);
      
      // Query by name only (email is in auth.users, not profiles)
      // Filter for non-system-managers (is_system_manager is false or null)
      const { data: rawData, error } = await supabase
        .from('profiles')
        .select('id, full_name, profile_photo_url')
        .ilike('full_name', searchPattern)
        .limit(50);
      
      // Filter out null full_name values in JavaScript
      const data = rawData?.filter(p => p.full_name && p.full_name.trim() !== '') || [];
      
      // Debug: Also try a simple query to see if we can get any homeowners at all
      if (data.length === 0 && !error) {
        console.log('No results with search pattern, trying simple query...');
        const { data: testData } = await supabase
          .from('profiles')
          .select('id, full_name, profile_photo_url, is_system_manager')
          .limit(10);
        console.log('Test query results (first 10 profiles):', testData);
        
        // Also try without the ilike to see if the filter is the issue
        const { data: testData2 } = await supabase
          .from('profiles')
          .select('id, full_name, profile_photo_url')
          .neq('is_system_manager', true)
          .limit(10);
        console.log('Test query 2 (non-system-managers, no name filter):', testData2);
      }

      console.log('Query executed, error:', error);
      console.log('Query data:', data);

      if (error) {
        console.error('Search error:', error);
        throw error;
      }

      console.log('Search results:', data?.length || 0, 'homeowners found');
      if (data && data.length > 0) {
        console.log('Found homeowners:', data.map(h => ({ id: h.id, name: h.full_name })));
      } else {
        console.log('No homeowners found matching:', trimmedQuery);
      }
      
      setAvailableHomeowners(data || []);
    } catch (error) {
      console.error('Error searching homeowners:', error);
      setAvailableHomeowners([]);
    } finally {
      setLoadingHomeowners(false);
    }
  };

  const handleToggleAIMode = async (conversationId: string, newMode: boolean) => {
    if (!conversationId) return;
    
    console.log(`Toggling AI mode for conversation ${conversationId} to ${newMode ? 'AI' : 'Human'}`);
    
    // Update local state optimistically
    setConversations(prev =>
      prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, ai_mode: newMode }
          : conv
      )
    );
    
    // Update selected conversation if it's the one being toggled
    if (selectedConversation?.id === conversationId) {
      setSelectedConversation(prev => prev ? { ...prev, ai_mode: newMode } : null);
    }
    
    // Persist to database
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ 
          ai_mode: newMode,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId);
      
      if (error) {
        console.error('Error updating AI mode:', error);
        // Revert optimistic update on error
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversationId
              ? { ...conv, ai_mode: !newMode }
              : conv
          )
        );
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation(prev => prev ? { ...prev, ai_mode: !newMode } : null);
        }
        showAlert('Failed to update conversation mode. Please try again.');
      } else {
        console.log('Successfully updated AI mode');
      }
    } catch (error) {
      console.error('Unexpected error updating AI mode:', error);
      // Revert optimistic update on error
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, ai_mode: !newMode }
            : conv
        )
      );
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(prev => prev ? { ...prev, ai_mode: !newMode } : null);
      }
      showAlert('Failed to update conversation mode. Please try again.');
    }
  };

  const handleStartConversation = async (homeownerId: string) => {
    try {
      // Check if a home-scoped conversation already exists.
      const existingConv = conversations.find(c => c.homeowner_id === homeownerId && c.home_id);
      if (existingConv) {
        setSelectedConversation(existingConv);
        setShowNewConversationModal(false);
        setHomeownerSearchQuery('');
        return;
      }

      // Create new conversation
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: homeownerHomes, error: homesError } = await supabase
        .from('homes')
        .select('id')
        .eq('owner_id', homeownerId)
        .order('created_at', { ascending: true });

      if (homesError) throw homesError;
      if (!homeownerHomes || homeownerHomes.length === 0) {
        showAlert('This homeowner has no homes yet. Add a home first from their profile.');
        return;
      }

      const targetHomeId = homeownerHomes[0].id;

      const { data: newConversation, error } = await supabase
        .from('conversations')
        .insert({
          homeowner_id: homeownerId,
          type: 'home_manager',
          home_manager_id: user.id,
          home_id: targetHomeId,
          ai_mode: false // Default to human mode
        })
        .select(`
          *,
          homeowner:profiles!conversations_homeowner_id_fkey (
            id,
            full_name,
            profile_photo_url
          ),
          home:homes!conversations_home_id_fkey (
            id,
            address,
            city,
            state
          )
        `)
        .single();

      if (error) throw error;

      // Add to conversations list and select it
      setConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConversation);
      setShowNewConversationModal(false);
      setHomeownerSearchQuery('');
      setAvailableHomeowners([]);
    } catch (error) {
      console.error('Error starting conversation:', error);
      showAlert('Failed to start conversation. Please try again.');
    }
  };

  useEffect(() => {
    console.log('useEffect triggered, modal:', showNewConversationModal, 'query:', homeownerSearchQuery);
    
    if (showNewConversationModal) {
      // Search when modal opens or query changes
      const timeoutId = setTimeout(() => {
        console.log('Timeout executed, query:', homeownerSearchQuery);
        if (homeownerSearchQuery.trim()) {
          searchHomeowners(homeownerSearchQuery);
        } else {
          console.log('Query is empty, clearing results');
          setAvailableHomeowners([]);
        }
      }, 300);

      return () => {
        console.log('Cleaning up timeout');
        clearTimeout(timeoutId);
      };
    } else {
      // Clear search when modal closes
      console.log('Modal closed, clearing search');
      setHomeownerSearchQuery('');
      setAvailableHomeowners([]);
    }
  }, [homeownerSearchQuery, showNewConversationModal]);

  return (
    <View style={styles.container}>
      {/* Left Panel - Conversations List */}
      {showListPanel && (
      <View style={[styles.leftPanel, isMobile && styles.leftPanelMobile]}>
        <View style={styles.leftPanelHeader}>
          <View style={styles.searchContainer}>
            <Search size={20} color="#6b7280" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search conversations..."
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <Pressable
            style={styles.newConversationButton}
            onPress={() => setShowNewConversationModal(true)}
          >
            <Plus size={20} color="#ffffff" />
            <Text style={styles.newConversationButtonText}>New</Text>
          </Pressable>
          {/* Mode Filter Buttons */}
          <View style={styles.filterContainer}>
            <Pressable
              style={[
                styles.filterButton,
                modeFilter === 'all' && styles.filterButtonActive
              ]}
              onPress={() => setModeFilter('all')}
            >
              <Text style={[
                styles.filterButtonText,
                modeFilter === 'all' && styles.filterButtonTextActive
              ]}>
                All
              </Text>
            </Pressable>
            <Pressable
              style={[
                styles.filterButton,
                modeFilter === 'human' && styles.filterButtonActive
              ]}
              onPress={() => setModeFilter('human')}
            >
              <User size={14} color={modeFilter === 'human' ? "#ffffff" : "#6b7280"} />
              <Text style={[
                styles.filterButtonText,
                modeFilter === 'human' && styles.filterButtonTextActive
              ]}>
                Human
              </Text>
            </Pressable>
            <Pressable
              style={[
                styles.filterButton,
                modeFilter === 'ai' && styles.filterButtonActive
              ]}
              onPress={() => setModeFilter('ai')}
            >
              <Bot size={14} color={modeFilter === 'ai' ? "#ffffff" : "#6b7280"} />
              <Text style={[
                styles.filterButtonText,
                modeFilter === 'ai' && styles.filterButtonTextActive
              ]}>
                AI
              </Text>
            </Pressable>
          </View>
        </View>

        <WebScrollView style={styles.conversationsList}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#000000" />
            </View>
          ) : filteredConversations.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No conversations</Text>
            </View>
          ) : (
            filteredConversations.map((conv) => (
              <Pressable
                key={conv.id}
                style={[
                  styles.conversationItem,
                  selectedConversation?.id === conv.id && styles.conversationItemActive
                ]}
                onPress={() => setSelectedConversation(conv)}
              >
                <View style={styles.conversationItemAvatar}>
                  {conv.homeowner.profile_photo_url ? (
                    <Image
                      source={{ uri: conv.homeowner.profile_photo_url }}
                      style={styles.conversationItemAvatarImage}
                    />
                  ) : (
                    <User size={20} color="#ffffff" />
                  )}
                </View>
                <View style={styles.conversationItemInfo}>
                  <View style={styles.conversationItemHeader}>
                    <View style={styles.conversationItemNameRow}>
                      <Text style={styles.conversationItemName}>
                        {conv.homeowner.full_name}
                      </Text>
                      {/* Mode Badge */}
                      <View style={[
                        styles.modeBadge,
                        conv.ai_mode ? styles.modeBadgeAI : styles.modeBadgeHuman
                      ]}>
                        {conv.ai_mode ? (
                          <>
                            <Bot size={12} color="#ffffff" />
                            <Text style={styles.modeBadgeText}>AI</Text>
                          </>
                        ) : (
                          <>
                            <User size={12} color="#ffffff" />
                            <Text style={styles.modeBadgeText}>Human</Text>
                          </>
                        )}
                      </View>
                      {/* Status Badge */}
                      {conv.status && conv.status !== 'active' && (
                        <View style={[
                          styles.statusBadge,
                          conv.status === 'completed' && styles.statusBadgeCompleted,
                          conv.status === 'failed' && styles.statusBadgeFailed,
                          conv.status === 'cancelled' && styles.statusBadgeCancelled,
                          conv.status === 'pending' && styles.statusBadgePending,
                          conv.status === 'no-response' && styles.statusBadgeNoResponse
                        ]}>
                          <Text style={styles.statusBadgeText}>{conv.status}</Text>
                        </View>
                      )}
                      {/* Human Takeover Indicator */}
                      {conv.human_takeover_active && (
                        <View style={styles.takeoverBadge}>
                          <User size={10} color="#ffffff" />
                        </View>
                      )}
                    </View>
                    {conv.last_message_at && (
                      <Text style={styles.conversationItemTime}>
                        {formatTimestamp(conv.last_message_at)}
                      </Text>
                    )}
                  </View>
                  {conv.home ? (
                    <Text style={styles.conversationItemHome} numberOfLines={1}>
                      {conv.home.address}, {conv.home.city}
                    </Text>
                  ) : null}
                  {conv.last_message_preview && (
                    <Text style={styles.conversationItemPreview} numberOfLines={1}>
                      {conv.last_message_preview}
                    </Text>
                  )}
                </View>
                {conv.manager_unread_count > 0 && (
                  <View style={styles.unreadBadge}>
                    <Text style={styles.unreadBadgeText}>{conv.manager_unread_count}</Text>
                  </View>
                )}
              </Pressable>
            ))
          )}
        </WebScrollView>
      </View>
      )}

      {/* Right Panel Container */}
      {showDetailPanel && (
      <KeyboardAvoidingView
        style={[styles.rightPanelContainer, isMobile && styles.rightPanelContainerMobile]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={keyboardVerticalOffset}
      >
        {/* Right Panel - Conversation Detail */}
        <View style={styles.rightPanel}>
          {selectedConversation ? (
          <>
            {/* Header */}
            <View style={styles.conversationHeader}>
              <View style={styles.conversationHeaderLeft}>
                {isMobile && (
                  <Pressable
                    style={styles.backButton}
                    onPress={() => {
                      setSelectedConversation(null);
                      setShowAISidebarMobile(false);
                    }}
                    accessibilityLabel="Back to conversations"
                  >
                    <ChevronLeft size={24} color="#000000" />
                  </Pressable>
                )}
                <View style={[styles.conversationHeaderAvatar, isMobile && styles.conversationHeaderAvatarMobile]}>
                  {selectedConversation.homeowner.profile_photo_url ? (
                    <Image
                      source={{ uri: selectedConversation.homeowner.profile_photo_url }}
                      style={styles.conversationHeaderAvatarImage}
                    />
                  ) : (
                    <User size={isMobile ? 20 : 24} color="#ffffff" />
                  )}
                </View>
                <View style={styles.conversationHeaderTextContainer}>
                  <Text style={styles.conversationHeaderName}>
                    {selectedConversation.homeowner.full_name}
                  </Text>
                  {/* Status and State */}
                  <View style={styles.conversationHeaderMeta}>
                    {selectedConversation.status && selectedConversation.status !== 'active' && (
                      <View style={[
                        styles.statusBadgeSmall,
                        selectedConversation.status === 'completed' && styles.statusBadgeCompleted,
                        selectedConversation.status === 'failed' && styles.statusBadgeFailed,
                        selectedConversation.status === 'cancelled' && styles.statusBadgeCancelled,
                        selectedConversation.status === 'pending' && styles.statusBadgePending,
                        selectedConversation.status === 'no-response' && styles.statusBadgeNoResponse
                      ]}>
                        <Text style={styles.statusBadgeTextSmall}>{selectedConversation.status}</Text>
                      </View>
                    )}
                    {selectedConversation.current_state && (
                      <Text style={styles.currentStateText}>{selectedConversation.current_state}</Text>
                    )}
                    {selectedConversation.human_takeover_active && (
                      <View style={styles.takeoverIndicator}>
                        <User size={12} color="#ef4444" />
                        <Text style={styles.takeoverText}>Human Takeover</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
              <View style={styles.conversationHeaderRight}>
                <Pressable
                  style={[styles.modeToggleButton, isMobile && styles.iconOnlyButton]}
                  onPress={() => handleToggleAIMode(selectedConversation.id, !selectedConversation.ai_mode)}
                  accessibilityLabel={selectedConversation.ai_mode ? 'AI Mode' : 'Human Mode'}
                >
                  {selectedConversation.ai_mode ? (
                    <>
                      <Bot size={isMobile ? 18 : 16} color="#3b82f6" />
                      {!isMobile && <Text style={styles.modeToggleButtonText}>AI Mode</Text>}
                    </>
                  ) : (
                    <>
                      <User size={isMobile ? 18 : 16} color="#6b7280" />
                      {!isMobile && <Text style={styles.modeToggleButtonText}>Human Mode</Text>}
                    </>
                  )}
                </Pressable>
                <Pressable
                  style={[styles.markUnreadButton, isMobile && styles.iconOnlyButton]}
                  onPress={() => markMessagesAsUnread(selectedConversation.id)}
                  accessibilityLabel="Mark Unread"
                >
                  <Mail size={isMobile ? 18 : 18} color="#6b7280" />
                  {!isMobile && <Text style={styles.markUnreadButtonText}>Mark Unread</Text>}
                </Pressable>
                {isMobile && selectedConversation.ai_mode && (
                  <Pressable
                    style={[styles.markUnreadButton, styles.iconOnlyButton]}
                    onPress={() => setShowAISidebarMobile(true)}
                    accessibilityLabel="AI Agent details"
                  >
                    <Bot size={18} color="#3b82f6" />
                  </Pressable>
                )}
              </View>
            </View>

            {/* Messages */}
            <View style={styles.messagesWrapper}>
            <WebScrollView
              ref={messagesScrollRef as any}
              style={styles.messagesContainer}
              onScroll={handleMessagesScroll}
              scrollEventThrottle={64}
            >
              <View style={styles.messagesList}>
                {messagesLoading ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#000000" />
                  </View>
                ) : (
                  messages.map((message) => {
                    const isFromHomeowner = message.sender_type === 'homeowner';
                    const isFromManager = !isFromHomeowner && message.sender_type === 'manager';
                    return (
                      <View
                        key={message.id}
                        style={[
                          styles.messageBubble,
                          isFromHomeowner
                            ? styles.messageBubbleHomeowner
                            : styles.messageBubbleManager
                        ]}
                      >
                        {/* Show sender name for manager messages */}
                        {isFromManager && message.sender?.full_name && (
                          <Text style={styles.messageSenderName}>
                            {message.sender.full_name}
                          </Text>
                        )}
                        {message.content && (
                          <Text style={[
                            styles.messageContent,
                            !isFromHomeowner && styles.messageContentManager
                          ]}>
                            {message.content}
                          </Text>
                        )}
                        {message.attachment_url && (
                          <Pressable
                            style={styles.attachmentContainer}
                            onPress={() => openExternalLink(message.attachment_url!)}
                          >
                            {message.attachment_type?.startsWith('image/') ? (
                              <View style={styles.imageAttachmentContainer}>
                                <Image
                                  source={{ uri: message.attachment_url }}
                                  style={styles.imagePreview}
                                  resizeMode="cover"
                                />
                                {message.attachment_name && (
                                  <Text style={[
                                    styles.imageAttachmentName,
                                    !isFromHomeowner && styles.imageAttachmentNameManager
                                  ]} numberOfLines={1}>
                                    {message.attachment_name}
                                  </Text>
                                )}
                              </View>
                            ) : (
                              <View style={styles.attachmentInfo}>
                                <Text style={[
                                  styles.attachmentName,
                                  !isFromHomeowner && styles.attachmentNameManager
                                ]} numberOfLines={1}>
                                  {message.attachment_name || 'Attachment'}
                                </Text>
                                {message.attachment_size && (
                                  <Text style={[
                                    styles.attachmentSize,
                                    !isFromHomeowner && styles.attachmentSizeManager
                                  ]}>
                                    {(message.attachment_size / 1024).toFixed(1)} KB
                                  </Text>
                                )}
                              </View>
                            )}
                          </Pressable>
                        )}
                        <Text style={[
                          styles.messageTime,
                          !isFromHomeowner && styles.messageTimeManager
                        ]}>
                          {formatTimestamp(message.created_at)}
                        </Text>
                      </View>
                    );
                  })
                )}
              </View>
            </WebScrollView>
            {showScrollToLatest && (
              <Pressable
                onPress={handleScrollToLatestPress}
                style={styles.scrollToLatestButton}
                accessibilityLabel="Scroll to latest message"
              >
                {unreadNewMessageCount > 0 && (
                  <View style={styles.scrollToLatestBadge}>
                    <Text style={styles.scrollToLatestBadgeText}>
                      {unreadNewMessageCount > 99 ? '99+' : unreadNewMessageCount}
                    </Text>
                  </View>
                )}
                <ChevronDown size={20} color="#ffffff" />
              </Pressable>
            )}
            </View>

            {/* Input */}
            <View style={styles.inputContainer}>
              {Platform.OS === 'web' && (
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="*/*"
                />
              )}
              {selectedFile && (
                <View style={styles.selectedFileContainer}>
                  <Text style={styles.selectedFileName} numberOfLines={1}>
                    {selectedFile.name}
                  </Text>
                  <Pressable
                    onPress={() => setSelectedFile(null)}
                    style={styles.removeFileButton}
                  >
                    <X size={16} color="#6b7280" />
                  </Pressable>
                </View>
              )}
              <Pressable
                onPress={handleFileSelect}
                style={styles.attachButton}
                disabled={sendingMessage || uploadingFile}
              >
                <Paperclip size={20} color={sendingMessage || uploadingFile ? "#9ca3af" : "#6b7280"} />
              </Pressable>
              <TextInput
                style={styles.messageInput}
                placeholder={selectedFile ? "Add a message (optional)..." : "Type your message..."}
                value={messageInput}
                onChangeText={setMessageInput}
                multiline
                maxLength={2000}
                editable={!sendingMessage && !uploadingFile}
              />
              <Pressable
                onPress={handleSendMessage}
                disabled={(!messageInput.trim() && !selectedFile) || sendingMessage || uploadingFile}
                style={[
                  styles.sendButton,
                  ((!messageInput.trim() && !selectedFile) || sendingMessage || uploadingFile) && styles.sendButtonDisabled
                ]}
              >
                {uploadingFile ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Send size={20} color="#ffffff" />
                )}
              </Pressable>
            </View>
          </>
        ) : (
          <View style={styles.noSelectionState}>
            <Text style={styles.noSelectionText}>Select a conversation to view messages</Text>
          </View>
        )}
        </View>

        {/* AI Agent Sidebar (desktop inline) */}
        {selectedConversation?.ai_mode && !isMobile && (
          <View style={styles.aiSidebar}>
          <View style={styles.aiSidebarHeader}>
            <Bot size={20} color="#3b82f6" />
            <Text style={styles.aiSidebarTitle}>AI Agent</Text>
          </View>
          
          <WebScrollView style={styles.aiSidebarContent}>
            {/* Status Indicator */}
            <View style={styles.aiStatusSection}>
              <Text style={styles.aiSectionTitle}>Status</Text>
              <View style={styles.aiStatusBadge}>
                <View style={[
                  styles.aiStatusDot,
                  selectedConversation.status === 'active' && styles.aiStatusDotActive,
                  selectedConversation.status === 'completed' && styles.aiStatusDotCompleted,
                  selectedConversation.status === 'failed' && styles.aiStatusDotFailed,
                  selectedConversation.status === 'pending' && styles.aiStatusDotPending
                ]} />
                <Text style={styles.aiStatusText}>
                  {selectedConversation.status || 'active'}
                </Text>
              </View>
              {selectedConversation.current_state && (
                <View style={styles.aiStateContainer}>
                  <Text style={styles.aiStateLabel}>Current State:</Text>
                  <Text style={styles.aiStateValue}>{selectedConversation.current_state}</Text>
                </View>
              )}
            </View>

            {/* Conversation Stats */}
            <View style={styles.aiSection}>
              <Text style={styles.aiSectionTitle}>Stats</Text>
              <View style={styles.aiStatsContainer}>
                <View style={styles.aiStatItem}>
                  <Text style={styles.aiStatLabel}>Messages</Text>
                  <Text style={styles.aiStatValue}>{selectedConversation.message_count || 0}</Text>
                </View>
                <View style={styles.aiStatItem}>
                  <Text style={styles.aiStatLabel}>Complexity</Text>
                  <Text style={styles.aiStatValue}>{selectedConversation.complexity_score || 0}</Text>
                </View>
                {selectedConversation.started_at && (
                  <View style={styles.aiStatItem}>
                    <Text style={styles.aiStatLabel}>Started</Text>
                    <Text style={styles.aiStatValueSmall}>
                      {new Date(selectedConversation.started_at).toLocaleDateString()}
                    </Text>
                  </View>
                )}
              </View>
            </View>

            {/* Toggle to Human Mode */}
            <View style={styles.aiActionSection}>
              <Pressable
                style={styles.aiActionButton}
                onPress={() => handleToggleAIMode(selectedConversation.id, false)}
              >
                <User size={16} color="#6b7280" />
                <Text style={styles.aiActionButtonText}>Switch to Human Mode</Text>
              </Pressable>
            </View>

            {/* Recent AI Actions */}
            <View style={styles.aiSection}>
              <Text style={styles.aiSectionTitle}>Recent AI Actions</Text>
              <View style={styles.aiEmptyState}>
                <Text style={styles.aiEmptyText}>No recent actions</Text>
              </View>
            </View>

            {/* AI Suggestions */}
            <View style={styles.aiSection}>
              <Text style={styles.aiSectionTitle}>AI Suggestions</Text>
              {selectedConversation.pending_ai_suggestion ? (
                <View style={styles.aiSuggestionContainer}>
                  <Text style={styles.aiSuggestionText}>
                    {selectedConversation.pending_ai_suggestion}
                  </Text>
                </View>
              ) : (
                <View style={styles.aiEmptyState}>
                  <Text style={styles.aiEmptyText}>No suggestions available</Text>
                </View>
              )}
            </View>

            {/* Human Takeover Info */}
            {selectedConversation.human_takeover_active && (
              <View style={styles.aiSection}>
                <Text style={styles.aiSectionTitle}>Human Takeover</Text>
                <View style={styles.aiTakeoverInfo}>
                  {selectedConversation.human_takeover_at && (
                    <Text style={styles.aiTakeoverText}>
                      Taken over: {new Date(selectedConversation.human_takeover_at).toLocaleString()}
                    </Text>
                  )}
                  {selectedConversation.human_takeover_reason && (
                    <Text style={styles.aiTakeoverReason}>
                      Reason: {selectedConversation.human_takeover_reason}
                    </Text>
                  )}
                </View>
              </View>
            )}

            {/* Error Message */}
            {selectedConversation.error_message && (
              <View style={styles.aiSection}>
                <Text style={styles.aiSectionTitle}>Error</Text>
                <View style={styles.aiErrorContainer}>
                  <Text style={styles.aiErrorText}>{selectedConversation.error_message}</Text>
                </View>
              </View>
            )}
          </WebScrollView>
          </View>
        )}
      </KeyboardAvoidingView>
      )}

      {/* AI Agent Sidebar (mobile slide-over) */}
      {isMobile && selectedConversation?.ai_mode && (
        <Modal
          visible={showAISidebarMobile}
          transparent
          animationType="slide"
          onRequestClose={() => setShowAISidebarMobile(false)}
        >
          <View style={styles.aiSidebarMobileOverlay}>
            <View style={styles.aiSidebarMobileContainer}>
              <View style={styles.aiSidebarHeader}>
                <Bot size={20} color="#3b82f6" />
                <Text style={styles.aiSidebarTitle}>AI Agent</Text>
                <Pressable
                  onPress={() => setShowAISidebarMobile(false)}
                  style={styles.aiSidebarCloseButton}
                  accessibilityLabel="Close AI Agent panel"
                >
                  <X size={22} color="#6b7280" />
                </Pressable>
              </View>

              <WebScrollView style={styles.aiSidebarContent}>
                <View style={styles.aiStatusSection}>
                  <Text style={styles.aiSectionTitle}>Status</Text>
                  <View style={styles.aiStatusBadge}>
                    <View style={[
                      styles.aiStatusDot,
                      selectedConversation.status === 'active' && styles.aiStatusDotActive,
                      selectedConversation.status === 'completed' && styles.aiStatusDotCompleted,
                      selectedConversation.status === 'failed' && styles.aiStatusDotFailed,
                      selectedConversation.status === 'pending' && styles.aiStatusDotPending
                    ]} />
                    <Text style={styles.aiStatusText}>
                      {selectedConversation.status || 'active'}
                    </Text>
                  </View>
                  {selectedConversation.current_state && (
                    <View style={styles.aiStateContainer}>
                      <Text style={styles.aiStateLabel}>Current State:</Text>
                      <Text style={styles.aiStateValue}>{selectedConversation.current_state}</Text>
                    </View>
                  )}
                </View>

                <View style={styles.aiSection}>
                  <Text style={styles.aiSectionTitle}>Stats</Text>
                  <View style={styles.aiStatsContainer}>
                    <View style={styles.aiStatItem}>
                      <Text style={styles.aiStatLabel}>Messages</Text>
                      <Text style={styles.aiStatValue}>{selectedConversation.message_count || 0}</Text>
                    </View>
                    <View style={styles.aiStatItem}>
                      <Text style={styles.aiStatLabel}>Complexity</Text>
                      <Text style={styles.aiStatValue}>{selectedConversation.complexity_score || 0}</Text>
                    </View>
                    {selectedConversation.started_at && (
                      <View style={styles.aiStatItem}>
                        <Text style={styles.aiStatLabel}>Started</Text>
                        <Text style={styles.aiStatValueSmall}>
                          {new Date(selectedConversation.started_at).toLocaleDateString()}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>

                <View style={styles.aiActionSection}>
                  <Pressable
                    style={styles.aiActionButton}
                    onPress={() => {
                      handleToggleAIMode(selectedConversation.id, false);
                      setShowAISidebarMobile(false);
                    }}
                  >
                    <User size={16} color="#6b7280" />
                    <Text style={styles.aiActionButtonText}>Switch to Human Mode</Text>
                  </Pressable>
                </View>

                <View style={styles.aiSection}>
                  <Text style={styles.aiSectionTitle}>AI Suggestions</Text>
                  {selectedConversation.pending_ai_suggestion ? (
                    <View style={styles.aiSuggestionContainer}>
                      <Text style={styles.aiSuggestionText}>
                        {selectedConversation.pending_ai_suggestion}
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.aiEmptyState}>
                      <Text style={styles.aiEmptyText}>No suggestions available</Text>
                    </View>
                  )}
                </View>

                {selectedConversation.human_takeover_active && (
                  <View style={styles.aiSection}>
                    <Text style={styles.aiSectionTitle}>Human Takeover</Text>
                    <View style={styles.aiTakeoverInfo}>
                      {selectedConversation.human_takeover_at && (
                        <Text style={styles.aiTakeoverText}>
                          Taken over: {new Date(selectedConversation.human_takeover_at).toLocaleString()}
                        </Text>
                      )}
                      {selectedConversation.human_takeover_reason && (
                        <Text style={styles.aiTakeoverReason}>
                          Reason: {selectedConversation.human_takeover_reason}
                        </Text>
                      )}
                    </View>
                  </View>
                )}

                {selectedConversation.error_message && (
                  <View style={styles.aiSection}>
                    <Text style={styles.aiSectionTitle}>Error</Text>
                    <View style={styles.aiErrorContainer}>
                      <Text style={styles.aiErrorText}>{selectedConversation.error_message}</Text>
                    </View>
                  </View>
                )}
              </WebScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* New Conversation Modal */}
      <Modal
        visible={showNewConversationModal}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setShowNewConversationModal(false);
          setHomeownerSearchQuery('');
          setAvailableHomeowners([]);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Start New Conversation</Text>
              <Pressable
                onPress={() => {
                  setShowNewConversationModal(false);
                  setHomeownerSearchQuery('');
                  setAvailableHomeowners([]);
                }}
                style={styles.modalCloseButton}
              >
                <X size={24} color="#6b7280" />
              </Pressable>
            </View>

            <View style={styles.modalSearchContainer}>
              <Search size={20} color="#6b7280" style={styles.modalSearchIcon} />
              <TextInput
                style={styles.modalSearchInput}
                placeholder="Search by name..."
                value={homeownerSearchQuery}
                onChangeText={setHomeownerSearchQuery}
                autoFocus
              />
            </View>

            <WebScrollView style={styles.modalResultsList}>
              {loadingHomeowners ? (
                <View style={styles.modalLoadingContainer}>
                  <ActivityIndicator size="large" color="#000000" />
                  <Text style={styles.modalLoadingText}>Searching...</Text>
                </View>
              ) : availableHomeowners.length === 0 && homeownerSearchQuery.trim() ? (
                <View style={styles.modalEmptyState}>
                  <Text style={styles.modalEmptyText}>No homeowners found</Text>
                  <Text style={styles.modalEmptySubtext}>
                    Try a different search term or check the console for details
                  </Text>
                </View>
              ) : availableHomeowners.length === 0 ? (
                <View style={styles.modalEmptyState}>
                  <Text style={styles.modalEmptyText}>Start typing to search for homeowners</Text>
                </View>
              ) : (
                availableHomeowners.map((homeowner) => (
                  <Pressable
                    key={homeowner.id}
                    style={styles.modalResultItem}
                    onPress={() => handleStartConversation(homeowner.id)}
                  >
                    <View style={styles.modalResultAvatar}>
                      {homeowner.profile_photo_url ? (
                        <Image
                          source={{ uri: homeowner.profile_photo_url }}
                          style={styles.modalResultAvatarImage}
                        />
                      ) : (
                        <User size={20} color="#ffffff" />
                      )}
                    </View>
                    <View style={styles.modalResultInfo}>
                      <Text style={styles.modalResultName}>{homeowner.full_name}</Text>
                    </View>
                  </Pressable>
                ))
              )}
            </WebScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
  },
  leftPanel: {
    width: Platform.OS === 'web' ? 350 : 300,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  leftPanelMobile: {
    flex: 1,
    width: '100%',
    borderRightWidth: 0,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  searchIcon: {
    position: 'absolute',
    left: 24,
    zIndex: 1,
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    paddingHorizontal: 40,
    paddingVertical: 8,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  conversationsList: {
    flex: 1,
  },
  conversationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  conversationItemActive: {
    backgroundColor: '#f3f4f6',
  },
  conversationItemAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#10b981',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
  },
  conversationItemAvatarImage: {
    width: '100%',
    height: '100%',
  },
  conversationItemInfo: {
    flex: 1,
  },
  conversationItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  conversationItemNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  modeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  modeBadgeAI: {
    backgroundColor: '#3b82f6',
  },
  modeBadgeHuman: {
    backgroundColor: '#6b7280',
  },
  modeBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },
  statusBadgeCompleted: {
    backgroundColor: '#10b981',
  },
  statusBadgeFailed: {
    backgroundColor: '#ef4444',
  },
  statusBadgeCancelled: {
    backgroundColor: '#6b7280',
  },
  statusBadgePending: {
    backgroundColor: '#f59e0b',
  },
  statusBadgeNoResponse: {
    backgroundColor: '#8b5cf6',
  },
  statusBadgeText: {
    fontSize: 9,
    fontWeight: '600',
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  statusBadgeSmall: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  statusBadgeTextSmall: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  takeoverBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
  },
  conversationItemName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000000',
  },
  conversationItemTime: {
    fontSize: 12,
    color: '#9ca3af',
  },
  conversationItemPreview: {
    fontSize: 14,
    color: '#6b7280',
  },
  conversationItemHome: {
    fontSize: 12,
    color: '#9ca3af',
  },
  unreadBadge: {
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    marginLeft: 8,
  },
  unreadBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  rightPanelContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    minWidth: 0,
  },
  rightPanelContainerMobile: {
    flex: 1,
    width: '100%',
  },
  rightPanel: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  conversationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    gap: 8,
  },
  conversationHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  conversationHeaderTextContainer: {
    flex: 1,
    minWidth: 0,
  },
  conversationHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 4,
  },
  iconOnlyButton: {
    width: 36,
    height: 36,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 0,
  },
  conversationHeaderAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#10b981',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
  },
  conversationHeaderAvatarMobile: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 10,
  },
  conversationHeaderAvatarImage: {
    width: '100%',
    height: '100%',
  },
  conversationHeaderName: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
  },
  conversationHeaderMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  currentStateText: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  takeoverIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: '#fee2e2',
  },
  takeoverText: {
    fontSize: 11,
    color: '#ef4444',
    fontWeight: '500',
  },
  markUnreadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
  },
  markUnreadButtonText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  modeToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
  },
  modeToggleButtonText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  messagesWrapper: {
    flex: 1,
    position: 'relative',
  },
  messagesContainer: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  messagesList: {
    padding: 16,
  },
  scrollToLatestButton: {
    position: 'absolute',
    bottom: 16,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#000000',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)' as any,
    }),
  },
  scrollToLatestBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollToLatestBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '700',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: 12,
    borderRadius: 12,
    marginBottom: 12,
  },
  messageBubbleHomeowner: {
    backgroundColor: '#e5e7eb',
    alignSelf: 'flex-start',
  },
  messageBubbleManager: {
    backgroundColor: '#000000',
    alignSelf: 'flex-end',
  },
  messageContent: {
    fontSize: 15,
    lineHeight: 20,
    marginBottom: 4,
    color: '#000000',
  },
  messageContentManager: {
    color: '#ffffff',
  },
  messageSenderName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff',
    opacity: 0.8,
    marginBottom: 4,
  },
  messageTime: {
    fontSize: 11,
    opacity: 0.6,
    color: '#000000',
  },
  messageTimeManager: {
    color: '#ffffff',
    opacity: 0.7,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
    backgroundColor: '#ffffff',
  },
  messageInput: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 15,
    maxHeight: 100,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#e5e7eb',
  },
  attachButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedFileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 8,
    gap: 8,
  },
  selectedFileName: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
  },
  removeFileButton: {
    padding: 4,
  },
  attachmentContainer: {
    marginTop: 8,
    marginBottom: 4,
    padding: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  attachmentInfo: {
    gap: 4,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#000000',
  },
  attachmentNameManager: {
    color: '#ffffff',
  },
  attachmentSize: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentSizeManager: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  imageAttachmentContainer: {
    gap: 8,
  },
  imagePreview: {
    width: '100%',
    maxWidth: 300,
    height: 200,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  imageAttachmentName: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  imageAttachmentNameManager: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    color: '#6b7280',
  },
  noSelectionState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noSelectionText: {
    fontSize: 16,
    color: '#6b7280',
  },
  leftPanelHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingBottom: 12,
    gap: 8,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  filterButtonActive: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  filterButtonText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: '#ffffff',
  },
  newConversationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    paddingHorizontal: 16,
    paddingVertical: 8,
    margin: 12,
    borderRadius: 8,
    gap: 6,
  },
  newConversationButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: Platform.OS === 'web' ? 500 : '90%',
    maxHeight: '80%',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalSearchIcon: {
    position: 'absolute',
    left: 28,
    zIndex: 1,
  },
  modalSearchInput: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    paddingHorizontal: 40,
    paddingVertical: 10,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  modalResultsList: {
    maxHeight: 400,
  },
  modalLoadingContainer: {
    padding: 40,
    alignItems: 'center',
    gap: 12,
  },
  modalLoadingText: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 8,
  },
  modalEmptyState: {
    padding: 40,
    alignItems: 'center',
  },
  modalEmptyText: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 4,
  },
  modalEmptySubtext: {
    fontSize: 12,
    color: '#9ca3af',
  },
  modalResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  modalResultAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#10b981',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
  },
  modalResultAvatarImage: {
    width: '100%',
    height: '100%',
  },
  modalResultInfo: {
    flex: 1,
  },
  modalResultName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 2,
  },
  aiSidebar: {
    width: Platform.OS === 'web' ? 350 : 300,
    backgroundColor: '#ffffff',
    borderLeftWidth: 1,
    borderLeftColor: '#e5e7eb',
    flexDirection: 'column',
  },
  aiSidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  aiSidebarTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
  },
  aiSidebarContent: {
    flex: 1,
  },
  aiStatusSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  aiSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  aiSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 12,
  },
  aiStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  aiStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10b981',
  },
  aiStatusDotActive: {
    backgroundColor: '#10b981',
  },
  aiStatusDotCompleted: {
    backgroundColor: '#10b981',
  },
  aiStatusDotFailed: {
    backgroundColor: '#ef4444',
  },
  aiStatusDotPending: {
    backgroundColor: '#f59e0b',
  },
  aiStatusText: {
    fontSize: 14,
    color: '#6b7280',
  },
  aiStateContainer: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
  },
  aiStateLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '500',
    marginBottom: 4,
  },
  aiStateValue: {
    fontSize: 13,
    color: '#000000',
    fontWeight: '500',
  },
  aiStatsContainer: {
    gap: 8,
  },
  aiStatItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  aiStatLabel: {
    fontSize: 13,
    color: '#6b7280',
  },
  aiStatValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000000',
  },
  aiStatValueSmall: {
    fontSize: 11,
    fontWeight: '500',
    color: '#6b7280',
  },
  aiSuggestionContainer: {
    padding: 12,
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  aiSuggestionText: {
    fontSize: 13,
    color: '#1e40af',
    lineHeight: 18,
  },
  aiTakeoverInfo: {
    gap: 6,
  },
  aiTakeoverText: {
    fontSize: 12,
    color: '#6b7280',
  },
  aiTakeoverReason: {
    fontSize: 12,
    color: '#ef4444',
    fontStyle: 'italic',
  },
  aiErrorContainer: {
    padding: 12,
    backgroundColor: '#fee2e2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  aiErrorText: {
    fontSize: 12,
    color: '#991b1b',
    lineHeight: 16,
  },
  aiActionSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  aiActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  aiActionButtonText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  aiEmptyState: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  aiEmptyText: {
    fontSize: 13,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  aiSidebarMobileOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  aiSidebarMobileContainer: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  aiSidebarCloseButton: {
    marginLeft: 'auto',
    padding: 4,
  },
});

export default ConversationsTab;
